import { TableIcon } from "lucide-react";
import { useMemo, useState } from "react";

import ChooseEntityDialog from "@/components/ChooseEntityDialog";
import { getSpanDisplayName } from "@/components/TraceDetail/SpanDetails/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { Entity, EntityType, useAddTraceToTable, useListSheets } from "@/queries";
import type { SpanNode } from "@/types/spans";
import { displayToast } from "@/utils/toast";

interface AddTraceToDatasetButtonProps {
  traceId?: string;
  selectedSpan: SpanNode | null;
}

const AddTraceToDatasetButton = ({ traceId, selectedSpan }: AddTraceToDatasetButtonProps) => {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState<Entity<EntityType.SMART_TABLE> | undefined>();

  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId;

  const { mutateAsync: addTraceToTable, isLoading } = useAddTraceToTable();

  const isSpanMode = Boolean(selectedSpan);
  const selectedSpanName = selectedSpan ? getSpanDisplayName(selectedSpan) : "";

  const selectedTableId = selectedTable?.id !== undefined ? String(selectedTable.id) : undefined;
  const sheetsQuery = useListSheets(selectedTableId, { limit: 100 });

  const targetSheet = useMemo(() => {
    const sheets = sheetsQuery.data?.items;
    if (!sheets?.length) {
      return undefined;
    }

    return [...sheets].sort((a, b) => a.index - b.index)[0];
  }, [sheetsQuery.data?.items]);

  const canSubmit = !!traceId && !!workspaceId && !!selectedTableId && !!targetSheet && !isLoading;

  const buttonLabel = isSpanMode ? (
    <span className="inline-flex min-w-0 items-baseline gap-1">
      <span>Add Span</span>
      <span className="inline-flex min-w-0 items-baseline font-semibold" title={selectedSpanName}>
        <span>&quot;</span>
        <span className="max-w-[160px] truncate">{selectedSpanName}</span>
        <span>&quot;</span>
      </span>
      <span>to Table</span>
    </span>
  ) : (
    "Add Trace to Table"
  );

  const handleConfirm = async () => {
    if (!traceId || !workspaceId || !selectedTableId || !targetSheet) {
      return;
    }

    try {
      const response = await addTraceToTable({
        smart_table_id: selectedTableId,
        sheet_id: targetSheet.id,
        trace_id: traceId,
        ...(isSpanMode && selectedSpan ? { span_id: selectedSpan.span_id } : {}),
      });

      if (response.success) {
        displayToast(
          isSpanMode ? "Span added to table" : "Trace added to table",
          ToastType.success,
          `/workspace/${workspaceId}/smart-tables/${selectedTableId}?sheet=${targetSheet.id}`,
        );
        setDialogOpen(false);
        return;
      }

      displayToast(response.error ?? response.message ?? "Could not add to table", ToastType.error);
    } catch {
      displayToast("Could not add to table", ToastType.error);
    }
  };

  const dialogTitle = isSpanMode ? "Add Span to Table" : "Add Trace to Table";
  const dialogDescription = isSpanMode
    ? "Select a table to add this span as a new row."
    : "Select a table to add this trace as a new row.";

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setDialogOpen(true)}
        disabled={!traceId || !workspaceId || isLoading}
        className="gap-2"
      >
        <TableIcon className="h-4 w-4" />
        {buttonLabel}
      </Button>
      <Dialog onOpenChange={setDialogOpen} open={isDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <p className="text-sm text-gray-600">{dialogDescription}</p>
            <ChooseEntityDialog
              type={EntityType.SMART_TABLE}
              onSelect={setSelectedTable as (table: Entity) => void}
              selectedEntity={selectedTable as Entity | undefined}
              currentFolderId={selectedTable?.folder_id ?? null}
              readonly={isLoading}
            />
            {selectedTableId && sheetsQuery.isLoading && (
              <p className="text-xs text-gray-500">Loading sheets...</p>
            )}
            {selectedTableId && !sheetsQuery.isLoading && !targetSheet && (
              <p className="text-xs text-red-600">This table has no sheets available.</p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} isLoading={isLoading} disabled={!canSubmit}>
              Add
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddTraceToDatasetButton;
